/**
 * Program panel registry — V102-1-5 (ADR-0050). A program's manifest names
 * the program-specific tab body per station (`shell.panels`); this file maps
 * those ids to components. A station a manifest doesn't list renders
 * `GenericProgramPanel`, the period-driven body (Scope = boundary, Prepare /
 * Audit = assessment windows, Operate = the operate queue, Report = the
 * Calendar & trust center hand-off), so a new program needs no SPA code.
 *
 * Reads/writes: each panel's own (program lead or owner/admin for writes;
 * server-enforced).
 */
import { useState, type ComponentType, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Input } from '@aigovops/design-system';
import type { ProgramManifest } from '@aigovops/core';
import { createProgramPeriod, startProgramFieldwork, type ProgramCard } from '../../lib/api';
import { invalidationMap } from '../../lib/query-keys';
import { programTitle } from '../../lib/nav-config';
import { fmtProgramDate, type CertStage } from '../../lib/program-shell';
import { addMonthsMinusDay } from '../../lib/soc2';
import { AskAdmin } from '../AskAdmin';
import { BoundaryPanel } from './BoundaryPanel';
import { ComponentBoundary } from './ComponentBoundary';
import { usesComponentBoundary } from '../../lib/boundary-model';
import { FedrampPrepare } from './FedrampPrepare';
import { OperateQueue } from './OperateQueue';
import { MonitoringOperateCard } from '../monitoring/MonitoringOperateCard';
import { ReportPackages } from './ReportPackages';
import { AssessmentResults } from './AssessmentResults';
import { KsiReadinessPanel } from './KsiReadiness';
import { CmmcAudit, CmmcScorecard } from './CmmcScorecard';
import { CmmcGaps, CmmcRequirements, CmmcSubnav, type CmmcView } from './CmmcAssess';
import { cmmcPeriod } from '../../lib/cmmc-model';
import { ScopeStage } from '../../pages/programs/ScopeStage';
import { Soc2Readiness } from '../../pages/Soc2Readiness';
import { AiConformity, AiOperate, AiScope } from '../ai-program/AiProgramTabs';
import { ContestedDecisions, useRuleDecisions } from '../ai-program/ContestedDecisions';
import { groupByLaw, splitLaws } from '../../lib/contested-decisions';
import { GovrampAudit, GovrampProgram } from './GovrampProgram';
import { ClassAChecklist } from './ClassAChecklist';
import { useQuery } from '@tanstack/react-query';
import { isSweepProgram } from '@aigovops/core';
import { SubNav } from '../sweep/SubNav';
import { ProgramChanges } from '../sweep/ProgramChanges';
import { ProgramControlList } from '../sweep/ProgramControlList';
import { useRollupContext } from '../sweep/ProgramRollup';
import { getSweepReport, sweepKeys } from '../../lib/sweep-api';
import { currentStage } from '../../lib/sweep-model';
import { stationTitle } from '../../lib/program-shell';
import { Soc2Audit, Soc2Categories, Soc2ClassA, Soc2Prepare, ScreenLoading, useProgramScreen } from './Soc2Program';
import { IsoAudit, IsoOperate, IsoPrepare, IsoScope } from './IsoProgram';

export interface ProgramPanelProps {
  tab: CertStage;
  manifest: ProgramManifest;
  card: ProgramCard;
  slug: string;
  prefix: string;
  canManage: boolean;
  isAdmin: boolean;
  tabHref: (t: CertStage) => string;
  viewerId: string;
  names: ReadonlyMap<string, string>;
  role: string;
}

/** V98-5 review (type scale / footer): the related pages sit in one labelled row. */
function OpsLinks({ prefix }: { prefix: string }) {
  return (
    <nav className="gp-related" aria-label="Related">
      <span className="gp-related-label" aria-hidden="true">Related</span>
      <ul className="gp-links">
        <li><Link to={`${prefix}/findings`}>Findings</Link></li>
        <li><Link to={`${prefix}/incidents`}>Incidents</Link></li>
        <li><Link to={`${prefix}/exceptions`}>Risk acceptances</Link></li>
        <li><Link to={`${prefix}/evidence`}>Evidence</Link></li>
      </ul>
    </nav>
  );
}

function OperateQueueBlock({ manifest, slug, prefix, viewerId, role, canManage, names }: ProgramPanelProps) {
  return <>
    <OperateQueue slug={slug} prefix={prefix} program={manifest.key} me={viewerId} role={role} canManage={canManage} names={names} />
    {/* V98-5-7: the mock audit is an internal working document over the same period — an action, not an orphan heading. */}
    <div className="gp-actions" data-part="mock-audit-link"><Link className="gp-linkbtn" to={`${prefix}/programs/${manifest.key}/operate/mock-audit`}>Run a mock audit</Link></div>
  </>;
}

/** Active periods with an id at this tab (Operate = certified periods). */
const periodsAt = (card: ProgramCard, tab: CertStage) =>
  card.periods.filter((p) => p.active && p.period_id).filter((p) => (tab === 'operate' ? p.stage === 'certified' : p.stage === tab));

/**
 * The generic, period-driven tab body. `operateLead` heads Operate (FedRAMP:
 * persistent KSI validation). The manifest's `shell.operateGate` keeps
 * Operate locked until the program is certified.
 */
export function GenericProgramPanel(props: ProgramPanelProps & { operateLead?: ReactNode }) {
  const { tab, manifest: m, card, slug, prefix, canManage, tabHref, operateLead } = props;
  const [params] = useSearchParams();
  if (tab === 'scope') {
    // V103-4-5: GovRAMP / FedRAMP 20x scope is the component boundary; the systems + providers view stays one click away.
    if (usesComponentBoundary(m)) {
      // Built-UI #2: the systems / services addendum belongs to the Components view; the other Scope views stand alone.
      const subview = params.get('view');
      return <>
        <ComponentBoundary slug={slug} prefix={prefix} manifest={m} card={card} canManage={canManage} isAdmin={props.isAdmin} />
        {subview && subview !== 'components' ? null : <details className="gp-cb-more"><summary>Systems and external services</summary>
          <BoundaryPanel slug={slug} program={m.key} manifest={m} card={card} canManage={canManage} />
        </details>}
      </>;
    }
    return <BoundaryPanel slug={slug} program={m.key} manifest={m} card={card} canManage={canManage} />;
  }
  if (tab === 'report') {
    // V109-6-5: programs that issue OSCAL Assessment Results (GovRAMP) download them here.
    if (m.packages.kinds.includes('oscal_ar_program')) {
      return <>
        <AssessmentResults slug={slug} program={m.key} isAdmin={props.isAdmin} />
        <ul className="gp-links" aria-label="Report"><li><Link to={`${prefix}/calendar`}>Calendar & trust center</Link></li></ul>
      </>;
    }
    return <EmptyState title="Reports and the trust center" action={<Link to={`${prefix}/calendar`}>Open Calendar & trust center</Link>} />;
  }

  const atStage = periodsAt(card, tab);
  if (tab === 'operate') {
    // V98-5 review T5-H2: a gated program's readiness and operate queue live in Prepare until it is certified.
    if (m.shell.operateGate && !atStage.length) {
      return <EmptyState title={`Operate starts once ${m.shortLabel} is certified`}
        description={`${m.features.persistentValidation ? 'KSI readiness' : 'Readiness'} and gaps are in Prepare until then.`}
        action={<Link className="gp-linkbtn" to={tabHref('prepare')}>Go to Prepare</Link>} />;
    }
    // V98-5-1: the queue runs whenever there is something to keep compliant; the certified periods head it.
    return <>
      {atStage.length
        ? atStage.map((p) => <p key={p.period_id!} className="gp-tab-sub">{`${p.label} · certified`}</p>)
        : <p className="gp-tab-sub">Not certified yet · <Link to={tabHref('audit')}>Go to Audit</Link></p>}
      {operateLead ?? null}
      <OperateQueueBlock {...props} />
      <OpsLinks prefix={prefix} />
    </>;
  }
  if (atStage.length) {
    return (
      <ul className="gp-gap-list">
        {atStage.map((p) => (
          <li key={p.period_id!} className="gp-gap">
            <span><strong>{p.label}</strong>{p.window ? ` · ${fmtProgramDate(p.window.start)} – ${fmtProgramDate(p.window.end)}` : ''}</span>
            {tab === 'audit' && p.phase !== 'fieldwork'
              ? (canManage ? <FieldworkButton slug={slug} program={m.key} periodId={p.period_id!} label={p.label} /> : <AskAdmin slug={slug} compact verb="start fieldwork" what="runs the program" subject={`GAC Pro: start fieldwork for ${p.label}`} />)
              : <span className="gp-dim">{p.phase === 'fieldwork' ? 'Fieldwork' : 'Readiness'}</span>}
          </li>
        ))}
      </ul>
    );
  }
  // No window yet → open the first one.
  return canManage
    ? <StartPeriodForm slug={slug} program={m.key} maxMonths={m.period.maxMonths ?? 12} title={tab === 'prepare' ? 'Set the assessment window' : 'Open an assessment window'} />
    : <EmptyState title="No assessment window yet" action={<AskAdmin slug={slug} what="opens assessment windows" subject={`GAC Pro: open the ${programTitle(m)} assessment window`} />} />;
}

/**
 * SOC 2 on Prepare / Audit / Operate. V106-1-8: once the `soc2-readiness` switch is
 * approved (508) the points-of-focus panels run; before it, today's framework view
 * stays (Operate adds the queue either way).
 */
function Soc2ReadinessPanel(props: ProgramPanelProps) {
  const { tab, prefix, tabHref } = props;
  const screen = useProgramScreen(props.slug, 'soc2');
  if (screen.isLoading) return <ScreenLoading q={screen} label="SOC 2" />;
  // No screen (an older server, or it failed): today's framework view, which needs nothing new.
  const s = screen.data ?? null;
  const objective = s?.engine?.engine === 'objective';
  const p = s ? { slug: props.slug, prefix, card: props.card, canManage: props.canManage || s.viewer.can_manage, isAdmin: props.isAdmin, viewerId: props.viewerId, role: props.role, tabHref, screen: s } : null;
  if (p && objective && tab === 'prepare') return <Soc2Prepare {...p} />;
  if (p && objective && tab === 'audit') return <Soc2Audit {...p} />;
  const view = tab === 'audit' ? 'audit' : tab === 'operate' ? 'operate' : 'prepare';
  return <>
    {tab === 'operate' && p && s?.class_a ? <Soc2ClassA {...p} /> : null}
    {tab === 'operate' ? <OperateQueueBlock {...props} /> : null}
    {objective ? null : <Soc2Readiness embedded view={view} criteriaHref={tabHref('prepare')} />}
    {tab === 'operate' ? <OpsLinks prefix={prefix} /> : null}
  </>;
}

/** V106-1-8: SOC 2 Scope — the Trust Services Categories, then the boundary and the audit windows. */
function Soc2ScopePanel(props: ProgramPanelProps) {
  const screen = useProgramScreen(props.slug, 'soc2');
  if (screen.isLoading) return <ScreenLoading q={screen} label="SOC 2" />;
  const s = screen.data;
  if (!s) return <GenericProgramPanel {...props} />;
  return <div className="gr-page">
    <Soc2Categories slug={props.slug} prefix={props.prefix} card={props.card} canManage={props.canManage || s.viewer.can_manage} isAdmin={props.isAdmin} viewerId={props.viewerId} role={props.role} tabHref={props.tabHref} screen={s} />
    <GenericProgramPanel {...props} />
  </div>;
}

/** V106-5-6: ISO 42001 Scope / Prepare / Audit / Operate. No window yet → the generic body opens one. */
function isoPanel(which: 'scope' | 'prepare' | 'audit' | 'operate') {
  return function IsoPanel(props: ProgramPanelProps) {
    const screen = useProgramScreen(props.slug, 'iso-42001');
    if (screen.isLoading || (screen.error && !screen.data)) return <ScreenLoading q={screen} label="ISO 42001" />;
    const s = screen.data!;
    if (which !== 'scope' && !s.periods.length) return <GenericProgramPanel {...props} />;
    // The screen's viewer flags come from the same assignment rule as Today (one source for who may act).
    const p = { slug: props.slug, prefix: props.prefix, card: props.card, canManage: props.canManage || s.viewer.can_manage, isAdmin: props.isAdmin, viewerId: props.viewerId, role: props.role, tabHref: props.tabHref, screen: s };
    if (which === 'scope') return <IsoScope {...p} />;
    if (which === 'prepare') return <IsoPrepare {...p} />;
    if (which === 'audit') return <IsoAudit {...p} />;
    return <><IsoOperate {...p} /><OperateQueueBlock {...props} /><OpsLinks prefix={props.prefix} /></>;
  };
}
const IsoScopePanel = isoPanel('scope');
const IsoPreparePanel = isoPanel('prepare');
const IsoAuditPanel = isoPanel('audit');
const IsoOperatePanel = isoPanel('operate');

/**
 * U5: FedRAMP Prepare leads with KSI readiness, "Fix N gaps" and the program's overdue items.
 * V103-6-3: a Class A program's Prepare is the Class A checklist (the required rules, the
 * alternative-framework materials, the Class A clocks).
 */
function FedrampPreparePanel(props: ProgramPanelProps) {
  if (props.card.certification_class === 'A') {
    return <ClassAChecklist card={props.card} slug={props.slug} prefix={props.prefix} canManage={props.canManage} tabHref={props.tabHref} />;
  }
  const at = periodsAt(props.card, 'prepare');
  if (!at.length) return <GenericProgramPanel {...props} />;
  return <FedrampPrepare slug={props.slug} prefix={props.prefix} card={props.card} periodId={at[0]!.period_id!} />;
}

/** V98-5-8: persistent KSI validation leads FedRAMP Operate (glance first). */
function KsiOperatePanel(props: ProgramPanelProps) {
  return <GenericProgramPanel {...props} operateLead={<KsiReadinessPanel slug={props.slug} prefix={props.prefix} />} />;
}

/** V98-5-4: FedRAMP reports (OCR / SDR / package) go draft → freeze → sign here; dates and the trust center live in Calendar & trust center. */
function ReportPackagesPanel({ slug, prefix, card, viewerId, canManage, names, manifest, isAdmin }: ProgramPanelProps) {
  return <>
    <ReportPackages slug={slug} prefix={prefix} profileId={card.profile_id} me={viewerId} canManage={canManage} names={names} />
    {/* V109-6-5: the per-control OSCAL Assessment Results and POA&M from a sweep. */}
    {manifest.packages.kinds.includes('oscal_ar_program') ? <AssessmentResults slug={slug} program={manifest.key} isAdmin={isAdmin} /> : null}
    <ul className="gp-links" aria-label="Report"><li><Link to={`${prefix}/calendar`}>Calendar & trust center</Link></li></ul>
  </>;
}

/** V98-4-5: AI Regulatory — obligation sources + the laws × systems grid, in-force gaps, Conformity. */
/**
 * V117-1-10: the AI Regulatory Scope tab has two views — Laws and systems (the grid) and Decisions (contested laws,
 * `?view=decisions`). The sub-nav shows once a contested law affects the workspace (or the view is open).
 */
function AiScopePanel({ slug, card, canManage, isAdmin, viewerId, names, tabHref }: ProgramPanelProps) {
  const [params] = useSearchParams();
  const decisions = useRuleDecisions(slug);
  const view = params.get('view') === 'decisions' ? 'decisions' : 'overview';
  const base = tabHref('scope');
  const waiting = decisions.data ? splitLaws(groupByLaw(decisions.data.decisions)).waiting.length : 0;
  const showNav = view === 'decisions' || (decisions.data?.decisions.length ?? 0) > 0;
  return <>
    {showNav ? <SubNav label="Scope views" current={view} items={[
      { key: 'overview', label: 'Laws and systems', href: base },
      { key: 'decisions', label: 'Decisions', href: `${base}?view=decisions`, count: waiting || null, countLabel: 'waiting' },
    ]} /> : null}
    {view === 'decisions'
      ? <ContestedDecisions slug={slug} viewerId={viewerId} overviewHref={base} />
      : <AiScope slug={slug} card={card} canManage={canManage} isAdmin={isAdmin} viewerId={viewerId} names={names} />}
  </>;
}
function AiOperatePanel({ slug, prefix, card }: ProgramPanelProps) {
  return <AiOperate slug={slug} prefix={prefix} card={card} />;
}
function AiConformityPanel({ slug, prefix }: ProgramPanelProps) {
  return <AiConformity slug={slug} prefix={prefix} />;
}

/**
 * V102-8-2: CMMC Prepare leads with the scorecard for the open period (Prepare
 * window first, then Audit). No window yet → the generic body opens one.
 */
function CmmcScorecardPanel(props: ProgramPanelProps) {
  const period = cmmcPeriod(props.card);
  const [params] = useSearchParams();
  if (!period) return <GenericProgramPanel {...props} />;
  // V102-8-3 / 8-4: Scorecard · Requirements · Gaps are views of the same Prepare tab (?view=).
  const raw = params.get('view');
  const view: CmmcView = raw === 'gaps' || raw === 'requirements' ? raw : 'scorecard';
  const assess = { slug: props.slug, prefix: props.prefix, card: props.card, periodId: period.period_id, periodLabel: period.label,
    canUpload: props.canManage || ['owner', 'admin', 'editor'].includes(props.role), canManage: props.canManage, isAdmin: props.isAdmin,
    viewerId: props.viewerId, names: props.names };
  return <>
    <CmmcSubnav view={view} counts={null} />
    {view === 'gaps' ? <CmmcGaps {...assess} />
      : view === 'requirements' ? <CmmcRequirements {...assess} />
        : <CmmcScorecard slug={props.slug} prefix={props.prefix} card={props.card} periodId={period.period_id} periodLabel={period.label}
            tabHref={props.tabHref} isAdmin={props.isAdmin} canManage={props.canManage} viewerId={props.viewerId} names={props.names} />}
  </>;
}

/**
 * PR A F4: CMMC Audit is the EXISTING period, never a form for a second window:
 * the period with Start fieldwork (which pins the tests; program lead or
 * owner/admin, server-enforced) and the affirmation gate.
 */
function CmmcAuditPanel(props: ProgramPanelProps) {
  const period = cmmcPeriod(props.card);
  if (!period) return <GenericProgramPanel {...props} />;
  return <CmmcAudit slug={props.slug} prefix={props.prefix} card={props.card} periodId={period.period_id} tabHref={props.tabHref}
    canManage={props.canManage} isAdmin={props.isAdmin} viewerId={props.viewerId} />;
}

/** V102-3-4: CMMC Scope stage — three-column categorizer + live SSP §2.1 preview. */
function CmmcScopePanel(props: ProgramPanelProps) {
  return <ScopeStage slug={props.slug} program={props.manifest.key} card={props.card} canManage={props.canManage} prepareHref={props.tabHref('prepare')} />;
}

/**
 * V1035-1-2 / T1-B3: GovRAMP Prepare — shows readiness and gap-closure content. Separate
 * from the Operate panel so the two tabs render distinct content. Both use GovrampProgram
 * but Prepare is the active tab during the pre-certification and tier-pursuit phases
 * (Core → Ready → Authorized); phase: 'certified' does NOT mean Prepare is done —
 * it means the initial assessment passed, and the workspace may still be pursuing a
 * higher tier (Ready booking is a Prepare-era action).
 */
function GovrampPreparePanel(props: ProgramPanelProps) {
  const hasPrepareWork = props.card.periods.some((p) => p.stage === 'prepare' || p.stage === 'scope');
  if (!hasPrepareWork && props.card.periods.length > 0) {
    // Built-UI (Medium "Stage rail"): what is done is this period's Prepare, never the program as a whole.
    return (
      <EmptyState
        title="Prepare is done for this period"
        description="Monitoring for it runs in Operate."
        action={<Link className="gp-linkbtn" to={props.tabHref('operate')}>Go to Operate</Link>}
      />
    );
  }
  return <GovrampProgram card={props.card} slug={props.slug} prefix={props.prefix} canManage={props.canManage} role={props.role} names={props.names}
    fallback={<GenericProgramPanel {...props} />} />;
}

/**
 * V103-3-6: GovRAMP Operate — status, ConMon, readiness, open controls and the tier change.
 * No period yet → the generic body opens the first one.
 */
function GovrampProgramPanel(props: ProgramPanelProps) {
  return <GovrampProgram card={props.card} slug={props.slug} prefix={props.prefix} canManage={props.canManage} role={props.role} names={props.names}
    fallback={<GenericProgramPanel {...props} />} />;
}

/** V103-3-6: GovRAMP Audit — GovRAMP's decision (tier granted) and the Ready booking, then the period's fieldwork. */
function GovrampAuditPanel(props: ProgramPanelProps) {
  return <GovrampAudit card={props.card} slug={props.slug} canManage={props.canManage} fallback={<GenericProgramPanel {...props} />} />;
}

/** Panel id (manifest `shell.panels`) → tab body. Every id a manifest names must be here (V102-1-6). */
export const PROGRAM_PANELS: Readonly<Record<string, ComponentType<ProgramPanelProps>>> = Object.freeze({
  'soc2-readiness': Soc2ReadinessPanel,
  'soc2-scope': Soc2ScopePanel,
  'iso-scope': IsoScopePanel,
  'iso-prepare': IsoPreparePanel,
  'iso-audit': IsoAuditPanel,
  'iso-operate': IsoOperatePanel,
  'fedramp-prepare': FedrampPreparePanel,
  'ksi-operate': KsiOperatePanel,
  'report-packages': ReportPackagesPanel,
  'ai-scope': AiScopePanel,
  'ai-operate': AiOperatePanel,
  'ai-conformity': AiConformityPanel,
  'cmmc-scope': CmmcScopePanel,
  'cmmc-scorecard': CmmcScorecardPanel,
  'cmmc-audit': CmmcAuditPanel,
  'govramp-prepare': GovrampPreparePanel,
  'govramp-program': GovrampProgramPanel,
  'govramp-audit': GovrampAuditPanel,
});

/** The tab body for a program's station: its registered panel, else the generic one. */
export function ProgramPanel(props: ProgramPanelProps) {
  const id = props.manifest.shell.panels[props.tab];
  const Panel = (id ? PROGRAM_PANELS[id] : undefined) ?? GenericProgramPanel;
  // V109-5-4 (R-a): a sweep program's CURRENT stage has two views — Readiness and "Changes since the last sweep".
  const body = isSweepProgram(props.manifest) && props.tab === currentStage(props.card) ? <SweepStageViews {...props} Panel={Panel} /> : <Panel {...props} />;
  // V113-7-5: on Operate, the monitoring rollup for the program's systems (renders nothing where the program isn't system-scoped).
  if (props.tab !== 'operate') return body;
  return <><MonitoringOperateCard slug={props.slug} prefix={props.prefix} program={props.manifest.key} />{body}</>;
}

function SweepStageViews(props: ProgramPanelProps & { Panel: ComponentType<ProgramPanelProps> }) {
  const { Panel, ...rest } = props;
  const [params] = useSearchParams();
  const view = params.get('view') === 'changes' ? 'changes' : 'readiness';
  const rollup = useRollupContext();
  const report = useQuery({ queryKey: sweepKeys.report(props.slug, props.manifest.key), queryFn: () => getSweepReport(props.slug, props.manifest.key), retry: false, staleTime: 30_000 });
  const n = report.data?.changes.length ?? null;
  const base = props.tabHref(props.tab);
  return <>
    <SubNav label={`${stationTitle(props.manifest, props.tab)} views`} current={view} items={[
      { key: 'readiness', label: 'Readiness', href: base },
      { key: 'changes', label: 'Changes since the last sweep', href: `${base}?view=changes`, count: report.data?.run ? n : null, countLabel: 'changes' },
    ]} />
    {view === 'changes'
      ? <ProgramChanges slug={props.slug} prefix={props.prefix} program={props.manifest.key} stage={props.tab} canSweep={props.isAdmin} readinessHref={base} card={props.card} />
      : <>
        <Panel {...rest} />
        {/* Built-UI #2 / #3: under the roll-up, the approved filtered control list (`?status=`) replaces the old open-controls grid. */}
        {rollup ? <ProgramControlList slug={props.slug} prefix={props.prefix} m={rollup.m} card={rollup.card} data={rollup.data}
          canUpload={props.canManage || ['owner', 'admin', 'editor'].includes(props.role)} isAdmin={props.isAdmin} /> : null}
      </>}
  </>;
}

function FieldworkButton({ slug, program, periodId, label }: { slug: string; program: string; periodId: string; label: string }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await startProgramFieldwork(slug, program, periodId);
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    } catch (e) { setError(e instanceof Error ? e.message : 'Fieldwork could not start.'); }
    finally { setBusy(false); }
  };
  return <span className="gp-actions">
    <Button variant="secondary" aria-disabled={busy || undefined} onClick={() => void go()} aria-label={`Start fieldwork for ${label}`}>{busy ? 'Starting…' : 'Start fieldwork'}</Button>
    {error ? <Alert variant="error">{error}</Alert> : null}
  </span>;
}

export function StartPeriodForm({ slug, program, maxMonths, title }: { slug: string; program: string; maxMonths: number; title: string }) {
  const queryClient = useQueryClient();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [endTouched, setEndTouched] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); if (saving) return;
    const errs: Record<string, string> = {};
    if (!start) errs.start = 'Choose the first day of the window.';
    if (!end) errs.end = 'Choose the last day of the window.';
    else if (start && end < start) errs.end = 'Ends on or after the start.';
    else if (start && end > addMonthsMinusDay(start, maxMonths)) errs.end = `A window covers at most ${maxMonths} months.`;
    setErrors(errs); if (Object.keys(errs).length) return;
    setSaving(true); setFormError(null);
    try {
      await createProgramPeriod(slug, program, { period_start: start, period_end: end });
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    } catch (err) { setFormError(err instanceof Error ? err.message : 'The window could not be saved.'); }
    finally { setSaving(false); }
  };
  return (
    <Card>
      <form className="gp-inline-form" onSubmit={submit} noValidate aria-labelledby="start-period-title">
        <h3 id="start-period-title">{title}</h3>
        {formError ? <Alert variant="error">{formError}</Alert> : null}
        <Field id="period-start" label="Window start" required error={errors.start}>
          <Input type="date" value={start} onChange={(e) => { const v = e.currentTarget.value; setStart(v); if (v && !endTouched) setEnd(addMonthsMinusDay(v, maxMonths)); }} />
        </Field>
        <Field id="period-end" label="Window end" required hint={`Up to ${maxMonths} months after the start.`} error={errors.end}>
          <Input type="date" value={end} onChange={(e) => { setEnd(e.currentTarget.value); setEndTouched(true); }} />
        </Field>
        <div className="gp-actions">
          <Button type="submit" variant="primary" aria-disabled={saving || undefined}>{saving ? 'Saving…' : 'Save window'}</Button>
        </div>
      </form>
    </Card>
  );
}
